import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { API, getAuthHeaders } from "../context/AuthContext";
import { ChartBar, Clock, CheckCircle, XCircle } from "@phosphor-icons/react";
import logger from "../utils/logger";

function timeAgo(iso) {
  if (!iso) return "—";
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

function Bar({ value, max, color }) {
  const pct = max > 0 ? Math.min(100, (value / max) * 100) : 0;
  return (
    <div className="w-full h-1.5 rounded-full bg-white/5 overflow-hidden">
      <div className="h-full transition-all" style={{ width: `${pct}%`, background: color }} />
    </div>
  );
}

export default function SlaveActivity() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);

  const fetchActivity = useCallback((d) => {
    setLoading(true);
    axios
      .get(`${API}/admin/slave-activity?days=${d}`, { headers: getAuthHeaders() })
      .then((res) => setItems(res.data.items || []))
      .catch((err) => logger.error("Slave activity error:", err))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchActivity(days); }, [days, fetchActivity]);

  const maxActions = Math.max(1, ...items.map((i) => i.total_actions || 0));
  const totalAll = items.reduce((s, i) => s + (i.total_actions || 0), 0);

  return (
    <div className="rounded-md border border-white/10 p-5 mt-6" style={{ background: "#121212" }} data-testid="slave-activity-card">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-lg bg-[rgba(0,229,255,0.08)] flex items-center justify-center">
            <ChartBar size={18} weight="duotone" className="text-[#00E5FF]" />
          </div>
          <div>
            <h3 className="text-sm font-bold text-white" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
              Slave Admin Performance
            </h3>
            <p className="text-[10px] text-[#A1A1AA]">
              {totalAll} total actions across {items.length} admins · last {days} days
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1 p-0.5 rounded-md bg-white/[0.03] border border-white/10">
          {[7, 30, 90].map((d) => (
            <button
              key={d}
              onClick={() => setDays(d)}
              data-testid={`slave-activity-range-${d}`}
              className={`px-2.5 py-1 rounded text-[10px] font-bold transition-all ${
                days === d
                  ? "bg-[#00E5FF] text-black"
                  : "text-[#A1A1AA] hover:text-white"
              }`}
            >
              {d}D
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="h-20 flex items-center justify-center">
          <div className="w-5 h-5 border-2 border-[#00E5FF] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : items.length === 0 ? (
        <div className="py-8 text-center text-xs text-[#A1A1AA]">No slave admin activity yet</div>
      ) : (
        <div className="space-y-3">
          {items.map((a) => (
            <div
              key={a.user_id}
              data-testid={`slave-activity-row-${a.user_id}`}
              className="p-3 rounded-lg bg-white/[0.02] border border-white/5 hover:border-white/10 transition-colors"
            >
              <div className="flex items-center justify-between mb-2">
                <div>
                  <p className="text-sm text-white font-medium">{a.name || "Unnamed"}</p>
                  <p className="text-[10px] text-[#A1A1AA]">{a.email}</p>
                </div>
                <div className="text-right">
                  <p className="text-lg font-bold text-[#00E5FF] font-mono" data-testid={`slave-total-${a.user_id}`}>
                    {a.total_actions}
                  </p>
                  <p className="text-[9px] text-[#52525B] uppercase tracking-wider">actions</p>
                </div>
              </div>
              <Bar value={a.total_actions} max={maxActions} color="#00E5FF" />
              <div className="flex items-center gap-4 mt-2.5 text-[10px]">
                <span className="flex items-center gap-1 text-emerald-400">
                  <CheckCircle size={10} weight="fill" />
                  {a.approvals} approvals
                </span>
                <span className="flex items-center gap-1 text-rose-400">
                  <XCircle size={10} weight="fill" />
                  {a.rejections} rejections
                </span>
                <span className="flex items-center gap-1 text-[#A1A1AA] ml-auto">
                  <Clock size={10} /> {timeAgo(a.last_active)}
                </span>
              </div>
              <div className="flex items-center gap-3 mt-2 text-[9px] uppercase tracking-wider text-[#52525B]">
                <span>WD: <span className="text-[#A1A1AA] font-semibold">{a.withdrawals_actions}</span></span>
                <span>KYC: <span className="text-[#A1A1AA] font-semibold">{a.kyc_actions}</span></span>
                <span>MT5: <span className="text-[#A1A1AA] font-semibold">{a.mt5_actions}</span></span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
